import {
  Paper,
  Group,
  Text,
  Button,
  Badge,
  Stack,
  Skeleton,
  Alert,
  List,
} from '@mantine/core';
import { IconPlugConnected, IconPlugConnectedX, IconBrandGoogle, IconAlertCircle } from '@tabler/icons-react';
import { useInitiateOAuth, useDisconnectGoogleAds } from '@/hooks/useGoogleAds';
import type { GoogleAdsConnection } from '@/types/googleAds';

interface ConnectionPanelProps {
  connection: GoogleAdsConnection | undefined;
  isLoading: boolean;
}

export function ConnectionPanel({ connection, isLoading }: ConnectionPanelProps) {
  const initiateOAuth = useInitiateOAuth();
  const disconnect = useDisconnectGoogleAds();

  const handleConnect = async () => {
    try {
      const { authUrl } = await initiateOAuth.mutateAsync();
      window.location.href = authUrl;
    } catch {
      // Error handled by mutation
    }
  };

  const handleDisconnect = () => {
    if (window.confirm('Möchten Sie die Verbindung zu Google Ads wirklich trennen?')) {
      disconnect.mutate();
    }
  };

  if (isLoading) {
    return (
      <Paper withBorder p="md">
        <Group justify="space-between">
          <Skeleton height={24} width={200} />
          <Skeleton height={36} width={150} />
        </Group>
      </Paper>
    );
  }

  if (!connection?.connected) {
    return (
      <Paper withBorder p="lg">
        <Stack gap="md">
          <Group>
            <IconPlugConnectedX size={24} color="gray" />
            <div>
              <Text fw={500}>Nicht verbunden</Text>
              <Text size="sm" c="dimmed">
                Verbinden Sie Ihr Google Ads Konto, um mit der Analyse zu beginnen.
              </Text>
            </div>
          </Group>

          <Alert icon={<IconAlertCircle size={16} />} color="blue" title="Hinweis">
            <Text size="sm">
              Die Verbindung erfolgt über OAuth2. Sie werden zu Google weitergeleitet, um die Berechtigung
              zu erteilen. Wir erhalten dabei nur Lesezugriff auf Ihre Google Ads Daten.
            </Text>
          </Alert>

          <Button
            leftSection={<IconBrandGoogle size={18} />}
            onClick={handleConnect}
            loading={initiateOAuth.isPending}
            size="md"
            w="fit-content"
          >
            Mit Google Ads verbinden
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="md">
      <Group justify="space-between">
        <Group>
          <IconPlugConnected size={24} color="green" />
          <div>
            <Group gap="xs">
              <Text fw={500}>Verbunden</Text>
              <Badge color="green" variant="light" size="sm">
                {connection.customerIds.length} Konto{connection.customerIds.length !== 1 ? 'en' : ''}
              </Badge>
            </Group>
            <Text size="sm" c="dimmed">
              {connection.customerIds.length > 0 && (
                <>Zugriff auf: {connection.customerIds.map((c) => c.name).join(', ')}</>
              )}
            </Text>
          </div>
        </Group>

        <Group>
          {connection.expiresAt && (
            <Text size="sm" c="dimmed">
              Token gültig bis: {new Date(connection.expiresAt).toLocaleDateString('de-DE')}
            </Text>
          )}
          <Button
            variant="subtle"
            color="red"
            onClick={handleDisconnect}
            loading={disconnect.isPending}
            size="sm"
          >
            Trennen
          </Button>
        </Group>
      </Group>

      {connection.scopes.length > 0 && (
        <Stack gap="xs" mt="md">
          <Text size="sm" fw={500}>
            Berechtigungen:
          </Text>
          <List size="sm" c="dimmed">
            {connection.scopes.includes('https://www.googleapis.com/auth/adwords.readonly') && (
              <List.Item>Lesezugriff auf Google Ads Daten</List.Item>
            )}
          </List>
        </Stack>
      )}
    </Paper>
  );
}
